import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ArrowLeft, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { useResources } from '../hooks/useResources'
import { POLL_INTERVALS } from '../lib/constants'
import {
  Alert,
  ActivityIndicator,
  Badge,
  Button,
  Checkbox,
  ChangeSet,
  CodeBlock,
  CodeField,
  DataTable,
  Field,
  FormLayout,
  FormLayoutActions,
  FormLayoutRow,
  FormLayoutSection,
  Input,
  PageHeader,
  PageLayout,
  StatePanel,
  StatusBadge,
  SummaryStrip,
  Tabs,
} from '../components/common'
import { ResourceConfigReplacementModal } from '../components/resources/ResourceConfigReplacementModal'
import styles from './ResourceDetails.module.css'

function formatDateTime(value, fallback) {
  if (!value) return fallback
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? fallback : date.toLocaleString()
}

export function ResourceDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [queryInput, setQueryInput] = useState('')
  const [queryResult, setQueryResult] = useState(null)
  const [connectionResult, setConnectionResult] = useState(null)
  const [isExecuting, setIsExecuting] = useState(false)
  const [settingsDraft, setSettingsDraft] = useState(null)
  const [settingsErrors, setSettingsErrors] = useState({})
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)

  const { useResource, useResourceMetrics, useTestResource, useResourceQuery, useUpdateResource } = useResources()
  const {
    data: resource,
    isLoading: resourceLoading,
    error: resourceError,
    refetch: refetchResource,
  } = useResource(id)
  const {
    data: metricsData,
    isLoading: metricsLoading,
    isError: metricsError,
    refetch: refetchMetrics,
  } = useResourceMetrics(id, { refetchInterval: POLL_INTERVALS.MEDIUM })
  const testMutation = useTestResource()
  const queryMutation = useResourceQuery(id)
  const updateMutation = useUpdateResource()
  const isSavingSettings = updateMutation.isPending || updateMutation.isLoading

  useEffect(() => {
    if (!resource) return
    setSettingsDraft({
      name: resource.name || '',
      connection_pool_size: String(resource.connection_pool_size || 10),
      query_limit_per_minute: String(resource.query_limit_per_minute ?? 0),
      is_active: Boolean(resource.is_active),
    })
    setSettingsErrors({})
  }, [resource])

  const settingsDirty = useMemo(() => {
    if (!resource || !settingsDraft) return false
    return (
      settingsDraft.name !== (resource.name || '')
      || Number(settingsDraft.connection_pool_size) !== (resource.connection_pool_size || 10)
      || Number(settingsDraft.query_limit_per_minute) !== (resource.query_limit_per_minute ?? 0)
      || settingsDraft.is_active !== Boolean(resource.is_active)
    )
  }, [resource, settingsDraft])

  const handleTestConnection = async () => {
    setConnectionResult(null)
    try {
      const result = await testMutation.mutateAsync(id)
      setConnectionResult({ type: 'success', message: result?.message || 'Connection test passed.' })
    } catch (testError) {
      setConnectionResult({ type: 'error', message: testError.response?.data?.details || 'Connection test failed.' })
    }
  }

  const handleExecuteQuery = async () => {
    if (!queryInput.trim()) return
    if (!resource?.is_active) {
      toast.error('Resource is inactive')
      return
    }

    setIsExecuting(true)
    setQueryResult(null)
    try {
      let queryData
      if (resource.type === 'postgresql') queryData = { sql: queryInput, params: {} }
      else if (resource.type === 'redis') {
        const parts = queryInput.trim().split(' ')
        queryData = { command: parts[0], args: parts.slice(1) }
      } else if (resource.type === 'rest_api') queryData = { method: 'GET', path: queryInput, headers: {} }
      else if (resource.type === 'graphql') queryData = { query: queryInput, variables: {} }
      else queryData = { query: queryInput }

      const result = await queryMutation.mutateAsync(queryData)
      setQueryResult({ type: 'success', data: result.result })
    } catch (queryError) {
      setQueryResult({ type: 'error', message: queryError.response?.data?.details || 'Query execution failed' })
    } finally {
      setIsExecuting(false)
    }
  }

  const resetSettingsDraft = () => {
    if (!resource) return
    setSettingsDraft({
      name: resource.name || '',
      connection_pool_size: String(resource.connection_pool_size || 10),
      query_limit_per_minute: String(resource.query_limit_per_minute ?? 0),
      is_active: Boolean(resource.is_active),
    })
    setSettingsErrors({})
  }

  const validateSettings = () => {
    const nextErrors = {}
    const poolSize = Number(settingsDraft?.connection_pool_size)
    const queryLimit = Number(settingsDraft?.query_limit_per_minute)
    if (!settingsDraft?.name?.trim()) nextErrors.name = 'Resource name is required'
    if (!Number.isInteger(poolSize) || poolSize < 1 || poolSize > 100) {
      nextErrors.connection_pool_size = 'Connection pool size must be between 1 and 100'
    }
    if (!Number.isInteger(queryLimit) || queryLimit < 0) {
      nextErrors.query_limit_per_minute = 'Query limit must be 0 or greater'
    }
    setSettingsErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSaveSettings = async () => {
    if (!settingsDirty || !validateSettings()) return
    try {
      await updateMutation.mutateAsync({
        id,
        updates: {
          name: settingsDraft.name.trim(),
          connection_pool_size: Number(settingsDraft.connection_pool_size),
          query_limit_per_minute: Number(settingsDraft.query_limit_per_minute),
          is_active: settingsDraft.is_active,
        },
      })
      toast.success('Resource settings updated')
    } catch (updateError) {
      toast.error(updateError.response?.data?.error || 'Failed to update resource settings')
    }
  }

  const handleReplaceConfig = async (config) => {
    try {
      await updateMutation.mutateAsync({ id, updates: { config } })
      setIsConfigModalOpen(false)
      toast.success('Connection details replaced')
    } catch (updateError) {
      toast.error(updateError.response?.data?.error || 'Failed to replace connection details')
      throw updateError
    }
  }

  if (resourceLoading) {
    return (
      <PageLayout size="wide">
        <PageHeader title="Resource" />
        <StatePanel state="loading" title="Loading resource" description="Connection health and settings will appear here." />
      </PageLayout>
    )
  }

  if (resourceError || !resource) {
    return (
      <PageLayout size="wide">
        <PageHeader title="Resource" />
        <StatePanel
          state="error"
          title="Resource not found"
          description={resourceError?.message || 'The requested resource could not be found.'}
          action={<Button variant="secondary" onClick={() => navigate('/dashboard/resources')}>Back to resources</Button>}
          onRetry={refetchResource}
        />
      </PageLayout>
    )
  }

  const metrics = metricsData?.metrics
  const usageStats = formatUsageStats(metricsData?.usage_stats)
  const tabs = [
    { value: 'overview', label: 'Overview', panel: <Overview resource={resource} metrics={metrics} metricsLoading={metricsLoading} metricsError={metricsError} onRetry={refetchMetrics} /> },
    { value: 'query', label: 'Query executor', panel: <QueryPanel resource={resource} value={queryInput} onChange={setQueryInput} onExecute={handleExecuteQuery} result={queryResult} executing={isExecuting} /> },
    { value: 'config', label: 'Configuration', panel: <ConfigurationPanel resource={resource} draft={settingsDraft} setDraft={setSettingsDraft} errors={settingsErrors} dirty={settingsDirty} saving={isSavingSettings} onReset={resetSettingsDraft} onSave={handleSaveSettings} onReplace={() => setIsConfigModalOpen(true)} /> },
    { value: 'usage', label: 'Usage', panel: <UsagePanel stats={usageStats} /> },
  ]

  return (
    <PageLayout size="wide">
      <PageHeader
        title={resource.name}
        context={(
          <Button variant="ghost" size="compact" onClick={() => navigate('/dashboard/resources')}>
            <ArrowLeft size={16} aria-hidden="true" /> Back to resources
          </Button>
        )}
        status={(
          <span className={styles.headerStatus}>
            <StatusBadge auto status={resource.health_status || 'unknown'}>
              {resource.health_status || 'unknown'}
            </StatusBadge>
            <Badge compact>{resource.type}</Badge>
          </span>
        )}
        primaryAction={(
          <Button onClick={handleTestConnection} loading={testMutation.isPending || testMutation.isLoading}>
            <RefreshCw size={16} aria-hidden="true" /> Test connection
          </Button>
        )}
      />

      {testMutation.isPending || testMutation.isLoading ? (
        <ActivityIndicator status="running" label="Testing resource connection" detail="Waiting for the resource to respond." compact />
      ) : null}
      {connectionResult ? (
        <ActivityIndicator
          status={connectionResult.type === 'success' ? 'success' : 'failed'}
          label={connectionResult.message}
          compact
        />
      ) : null}

      <Tabs items={tabs} value={activeTab} onValueChange={setActiveTab} label="Resource sections" />

      <ResourceConfigReplacementModal
        open={isConfigModalOpen}
        resourceType={resource.type}
        onClose={() => setIsConfigModalOpen(false)}
        onSubmit={handleReplaceConfig}
        isSubmitting={isSavingSettings}
      />
    </PageLayout>
  )
}

function Overview({ resource, metrics, metricsLoading, metricsError, onRetry }) {
  const summaryItems = metrics ? [
    { key: 'queries', label: 'Queries', value: metrics.queries_executed || 0 },
    { key: 'bytes', label: 'Bytes processed', value: formatBytes(metrics.bytes_processed || 0) },
    { key: 'errors', label: 'Errors', value: metrics.error_count || 0 },
    { key: 'success', label: 'Success rate', value: calculateSuccessRate(metrics) },
    { key: 'latency', label: 'Average latency', value: metrics.latency ? `${metrics.latency.toFixed(2)}ms` : 'N/A' },
  ] : []

  return (
    <div className={styles.tabStack}>
      {metricsLoading ? <StatePanel state="loading" title="Loading metrics" compact /> : null}
      {metricsError ? <StatePanel state="error" title="Metrics are unavailable" onRetry={onRetry} compact /> : null}
      {!metricsLoading && !metricsError && metrics ? <SummaryStrip items={summaryItems} label="Resource health summary" /> : null}
      {!metricsLoading && !metricsError && !metrics ? <StatePanel state="empty" title="No metrics yet" description="Metrics appear after the resource handles requests." compact /> : null}

      <section className={styles.section}>
        <h2>Resource information</h2>
        <dl className={styles.definitionList}>
          <Info label="Resource ID" value={resource.id} technical />
          <Info label="Created" value={formatDateTime(resource.created_at, 'Not recorded')} />
          <Info label="Last health check" value={formatDateTime(resource.last_health_check, 'Never')} />
          <Info label="Connection pool size" value={resource.connection_pool_size || 'Default'} />
          <Info label="Query limit per minute" value={resource.query_limit_per_minute || 'Unlimited'} />
          <Info label="Status" value={resource.is_active ? 'Active' : 'Inactive'} />
        </dl>
      </section>

      {metrics?.last_error ? (
        <Alert variant="error">
          <strong>Last error</strong>
          <CodeBlock value={metrics.last_error} label="Last resource error" language="text" />
        </Alert>
      ) : null}
    </div>
  )
}

function QueryPanel({ resource, value, onChange, onExecute, result, executing }) {
  return (
    <FormLayout className={styles.queryPanel}>
      <FormLayoutSection title="Query executor" description={getQueryHelp(resource.type)}>
        <CodeField
          label="Query or command"
          language={resource.type === 'postgresql' ? 'sql' : resource.type === 'graphql' ? 'graphql' : resource.type === 'redis' ? 'shell' : 'text'}
          placeholder={getQueryPlaceholder(resource.type)}
          value={value}
          onChange={onChange}
          disabled={!resource.is_active}
          rows={10}
        />
        {!resource.is_active ? <Alert variant="warning">Resource is inactive.</Alert> : null}
        <FormLayoutActions>
          <Button onClick={onExecute} loading={executing} disabled={!value.trim() || !resource.is_active}>Execute query</Button>
        </FormLayoutActions>
      </FormLayoutSection>

      {executing ? (
        <ActivityIndicator status="running" label="Executing resource query" detail="The result will appear when the resource responds." />
      ) : null}

      {result ? (
        <div className={styles.tabStack}>
          <ActivityIndicator
            status={result.type === 'success' ? 'success' : 'failed'}
            label={result.type === 'success' ? 'Query completed' : 'Query failed'}
            detail={result.message}
          />
          {result.data ? <CodeBlock value={result.data} label="Query result" language="json" /> : null}
        </div>
      ) : null}
    </FormLayout>
  )
}

function ConfigurationPanel({ resource, draft, setDraft, errors, dirty, saving, onReset, onSave, onReplace }) {
  const changes = dirty ? [
    { label: 'Name', current: resource.name || 'Unnamed', proposed: draft?.name || 'Unnamed', impact: 'Changes the Studio identity' },
    { label: 'Connection pool', current: String(resource.connection_pool_size || 10), proposed: String(draft?.connection_pool_size || 10), impact: 'Changes concurrent connection capacity' },
    { label: 'Query limit', current: String(resource.query_limit_per_minute ?? 0), proposed: String(draft?.query_limit_per_minute ?? 0), impact: 'Changes the per-minute request ceiling' },
    { label: 'Status', current: resource.is_active ? 'Active' : 'Inactive', proposed: draft?.is_active ? 'Active' : 'Inactive', impact: draft?.is_active ? 'Queries remain available' : 'Queries are blocked' },
  ].filter((item) => item.current !== item.proposed) : []

  return (
    <FormLayout className={styles.configPanel}>
      <FormLayoutSection title="Connection details">
        <Alert variant="info">Stored connection details are encrypted and never displayed.</Alert>
        <FormLayoutActions>
          <Button variant="secondary" onClick={onReplace}>Replace connection details</Button>
        </FormLayoutActions>
      </FormLayoutSection>

      <FormLayoutSection title="Settings">
        <Field label="Resource Name" error={errors.name} required>
          <Input value={draft?.name || ''} onChange={(event) => setDraft((current) => ({ ...(current || {}), name: event.target.value }))} />
        </Field>
        <FormLayoutRow columns={2}>
          <Field label="Connection Pool Size" error={errors.connection_pool_size} required>
            <Input type="number" min="1" max="100" value={draft?.connection_pool_size || '10'} onChange={(event) => setDraft((current) => ({ ...(current || {}), connection_pool_size: event.target.value }))} />
          </Field>
          <Field label="Query Limit per Minute" help="Use 0 for unlimited." error={errors.query_limit_per_minute} required>
            <Input type="number" min="0" value={draft?.query_limit_per_minute ?? '0'} onChange={(event) => setDraft((current) => ({ ...(current || {}), query_limit_per_minute: event.target.value }))} />
          </Field>
        </FormLayoutRow>
        <Checkbox label="Active" description="Inactive resources cannot execute queries." checked={Boolean(draft?.is_active)} onChange={(event) => setDraft((current) => ({ ...(current || {}), is_active: event.target.checked }))} />

        <ChangeSet
          items={changes}
          title="Pending setting changes"
          description="Review the operational effect before saving."
        />

        <div className={styles.stickyFormActions}>
          {dirty ? <span className={styles.dirtyState} role="status">Unsaved changes</span> : <span className={styles.savedState}>No pending changes</span>}
          <Button variant="secondary" onClick={onReset} disabled={!dirty || saving}>Cancel</Button>
          <Button onClick={onSave} loading={saving} disabled={!dirty}>Save settings</Button>
        </div>
      </FormLayoutSection>
    </FormLayout>
  )
}

function UsagePanel({ stats }) {
  if (!stats) return <StatePanel state="empty" title="No usage statistics yet" description="Usage appears after the resource handles an operation." />

  const rows = Object.entries(stats.operationBreakdown).map(([operation, count]) => ({ operation, count }))
  return (
    <div className={styles.tabStack}>
      <SummaryStrip
        label="Resource usage summary"
        items={[
          { key: 'operations', label: 'Total operations', value: stats.totalOperations },
          { key: 'hours', label: 'Active hours', value: stats.activeHours },
          { key: 'first', label: 'First usage', value: stats.firstUsage },
          { key: 'last', label: 'Last usage', value: stats.lastUsage },
        ]}
      />
      <section className={styles.section}>
        <h2>Operation breakdown</h2>
        {rows.length > 0 ? (
          <DataTable
            label="Operation breakdown"
            density="compact"
            data={rows}
            keyField="operation"
            columns={[
              { key: 'operation', label: 'Operation' },
              { key: 'count', label: 'Count', numeric: true },
            ]}
          />
        ) : <StatePanel state="empty" title="No operations recorded" compact />}
      </section>
    </div>
  )
}

function Info({ label, value, technical = false }) {
  return <div><dt>{label}</dt><dd className={technical ? styles.technical : ''}>{value}</dd></div>
}

function formatUsageStats(stats) {
  if (!stats) return null
  return {
    totalOperations: stats.total_operations || 0,
    activeHours: stats.active_hours || 0,
    firstUsage: stats.first_usage ? new Date(stats.first_usage).toLocaleString() : 'Never',
    lastUsage: stats.last_usage ? new Date(stats.last_usage).toLocaleString() : 'Never',
    operationBreakdown: stats.operation_breakdown || {},
  }
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes'
  const base = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const index = Math.floor(Math.log(bytes) / Math.log(base))
  return `${Math.round((bytes / Math.pow(base, index)) * 100) / 100} ${sizes[index]}`
}

function calculateSuccessRate(metrics) {
  if (!metrics?.queries_executed) return '0%'
  const successCount = metrics.queries_executed - (metrics.error_count || 0)
  return `${((successCount / metrics.queries_executed) * 100).toFixed(1)}%`
}

function getQueryHelp(resourceType) {
  if (resourceType === 'postgresql') return 'Enter SQL to run against this resource.'
  if (resourceType === 'redis') return 'Enter a Redis command and arguments.'
  if (resourceType === 'rest_api') return 'Enter the endpoint path to request.'
  if (resourceType === 'graphql') return 'Enter a GraphQL query.'
  if (resourceType === 'gcs') return 'Enter a supported GCS operation.'
  return 'Enter a query supported by this resource.'
}

function getQueryPlaceholder(resourceType) {
  if (resourceType === 'postgresql') return 'SELECT * FROM users LIMIT 10;'
  if (resourceType === 'redis') return 'GET mykey'
  if (resourceType === 'rest_api') return '/api/users'
  if (resourceType === 'graphql') return '{ users { id name email } }'
  if (resourceType === 'gcs') return 'list my-bucket'
  return 'Enter your query here…'
}
